import { useState } from 'react';
import {
  CALC_METHOD_LABELS,
  CALC_STATUS_LABELS,
  type CalcMethodCode,
  type CalcRunDto,
  type CalcRunStatus,
  type MethodRunDto,
} from '../../lib/api-types';
import type { CalcPrepareInput } from '../../lib/api';
import { toDisplayError } from '../../lib/errors';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import {
  useAdjustPublishedValue,
  useCalculations,
  useExecuteCalculation,
  useFinalizeCalculation,
  usePrepareCalculation,
  useReviewCalculation,
  useValidateCalculation,
} from './hooks';

const ALL_METHODS: CalcMethodCode[] = ['COUT', 'COMPARAISON', 'CAPITALISATION', 'MOYENNE'];

/** Values a decimal-string field renders when the server hasn't produced one yet. */
function money(value: string | null | undefined): string {
  return value && value.length > 0 ? `${value} FCFA` : '—';
}

function statusTone(status: CalcRunStatus): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'BLOQUE':
    case 'ANNULE':
      return 'danger';
    case 'CALCULE':
    case 'VALIDE':
    case 'FINALISE':
      return 'success';
    case 'PREPARE':
    case 'EN_REVUE':
      return 'info';
    case 'REMPLACE':
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * « Calcul » — moteur de valorisation d'un dossier (LOT 6B). L'expert soumet son INTENTION
 * (date, méthodes, saisies) ; le SERVEUR résout les coûts/coefficients, choisit les replis, arrondit,
 * calcule et hache. Le Web n'effectue AUCUN calcul : il n'affiche que les valeurs renvoyées par l'API.
 */
export function CalculationTab({ dossierId }: { dossierId: string }): JSX.Element {
  const history = useCalculations(dossierId);
  const prepare = usePrepareCalculation(dossierId);
  const execute = useExecuteCalculation(dossierId);
  const review = useReviewCalculation(dossierId);
  const validate = useValidateCalculation(dossierId);
  const finalize = useFinalizeCalculation(dossierId);
  const adjust = useAdjustPublishedValue(dossierId);

  // The currently-active run — always seeded from the server's authoritative response.
  const [run, setRun] = useState<CalcRunDto | null>(null);

  // --- Preparation form state -------------------------------------------------
  const [valuationDate, setValuationDate] = useState('');
  const [methods, setMethods] = useState<CalcMethodCode[]>([]);
  const [cmpSuperficie, setCmpSuperficie] = useState('');
  const [cmpPrix, setCmpPrix] = useState('');
  const [cmpSet, setCmpSet] = useState('');
  const [costSet, setCostSet] = useState('');
  const [costBati, setCostBati] = useState('');
  const [costParcelle, setCostParcelle] = useState('');
  const [capBase, setCapBase] = useState('');
  const [capMultiplier, setCapMultiplier] = useState('');
  const [capSet, setCapSet] = useState('');
  const [capFactors, setCapFactors] = useState<string[]>(['']);

  // --- Published-value adjustment form state ----------------------------------
  const [publishedValue, setPublishedValue] = useState('');
  const [adjustJustification, setAdjustJustification] = useState('');

  const toggleMethod = (m: CalcMethodCode): void => {
    setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };

  const canPrepare = valuationDate !== '' && methods.length > 0 && !prepare.isPending;

  const buildPrepareInput = (): CalcPrepareInput => {
    const trimmed = (s: string): string => s.trim();
    return {
      valuationDate,
      methods,
      ...(methods.includes('COMPARAISON')
        ? {
            comparison: {
              superficieM2: trimmed(cmpSuperficie),
              prixFoncierM2: trimmed(cmpPrix),
              coefficientSetCode: trimmed(cmpSet),
            },
          }
        : {}),
      ...(methods.includes('COUT')
        ? {
            cost: {
              coefficientSetCode: trimmed(costSet),
              ...(trimmed(costBati) !== '' ? { batiArrondie: trimmed(costBati) } : {}),
              // Quand COMPARAISON est présente, la parcelle vient du marché : on n'envoie pas la
              // saisie manuelle (le champ est masqué), pour ne pas contredire ce que dit l'écran.
              ...(!methods.includes('COMPARAISON') && trimmed(costParcelle) !== ''
                ? { parcelleArrondie: trimmed(costParcelle) }
                : {}),
            },
          }
        : {}),
      ...(methods.includes('CAPITALISATION')
        ? {
            capitalization: {
              baseRevenu: trimmed(capBase),
              multiplier: trimmed(capMultiplier),
              revenuFactors: capFactors.map(trimmed).filter((f) => f !== ''),
              ...(trimmed(capSet) !== '' ? { coefficientSetCode: trimmed(capSet) } : {}),
            },
          }
        : {}),
    };
  };

  const runPrepare = (): void => {
    if (!canPrepare) return;
    prepare.mutate(buildPrepareInput(), { onSuccess: (data) => setRun(data) });
  };

  const runExecute = (): void => {
    if (!run || run.status !== 'PREPARE') return;
    execute.mutate({ calcRunId: run.id }, { onSuccess: (data) => setRun(data) });
  };

  const status = run?.status ?? null;
  const canAdjust =
    (status === 'CALCULE' || status === 'EN_REVUE') &&
    publishedValue.trim() !== '' &&
    adjustJustification.trim() !== '' &&
    !adjust.isPending;

  const runAdjust = (): void => {
    if (!run || !canAdjust) return;
    adjust.mutate(
      {
        id: run.id,
        input: { publishedValue: publishedValue.trim(), justification: adjustJustification.trim() },
      },
      {
        onSuccess: (data) => {
          setRun(data);
          setPublishedValue('');
          setAdjustJustification('');
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* A. PRÉPARATION ------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Préparation du calcul</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="calc-date">Date de valorisation</Label>
              <Input
                id="calc-date"
                type="date"
                value={valuationDate}
                onChange={(e) => setValuationDate(e.target.value)}
              />
            </div>
          </div>

          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium text-slate-700">Méthodes</legend>
            <div className="flex flex-wrap gap-4">
              {ALL_METHODS.map((m) => (
                <label key={m} className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={methods.includes(m)}
                    onChange={() => toggleMethod(m)}
                    aria-label={CALC_METHOD_LABELS[m]}
                  />
                  {CALC_METHOD_LABELS[m]}
                </label>
              ))}
            </div>
          </fieldset>

          {methods.includes('COMPARAISON') ? (
            <fieldset className="space-y-2 rounded-md border border-slate-200 p-3">
              <legend className="px-1 text-xs uppercase tracking-wide text-slate-400">
                Comparaison
              </legend>
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <Label htmlFor="cmp-sup">Superficie (m²)</Label>
                  <Input
                    id="cmp-sup"
                    inputMode="decimal"
                    value={cmpSuperficie}
                    onChange={(e) => setCmpSuperficie(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="cmp-prix">Prix foncier (m²)</Label>
                  <Input
                    id="cmp-prix"
                    inputMode="decimal"
                    value={cmpPrix}
                    onChange={(e) => setCmpPrix(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="cmp-set">Jeu de coefficients</Label>
                  <Input id="cmp-set" value={cmpSet} onChange={(e) => setCmpSet(e.target.value)} />
                </div>
              </div>
            </fieldset>
          ) : null}

          {methods.includes('COUT') ? (
            <fieldset className="space-y-2 rounded-md border border-slate-200 p-3">
              <legend className="px-1 text-xs uppercase tracking-wide text-slate-400">
                Coût (reconstruction)
              </legend>
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <Label htmlFor="cost-set">Jeu de coefficients</Label>
                  <Input
                    id="cost-set"
                    value={costSet}
                    onChange={(e) => setCostSet(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="cost-bati">Bâti arrondi (facultatif)</Label>
                  <Input
                    id="cost-bati"
                    inputMode="decimal"
                    value={costBati}
                    onChange={(e) => setCostBati(e.target.value)}
                  />
                </div>
                {/* LOT 9D (OPTION A) — la parcelle n'alimente QUE la valeur vénale (COUT.roundedValue).
                    Elle est PROUVÉE INERTE pour la mise en valeur, dont la valeur publiée est
                    batiArrondie (D-6). On l'affiche donc seulement quand elle sert :
                    - avec COMPARAISON : la parcelle vient du marché, le champ manuel est redondant → masqué ;
                    - sans COMPARAISON : requise par la méthode COUT complète, mais sans effet sur la mise
                      en valeur — dit explicitement, jamais pré-rempli (ce serait un faux calcul). */}
                {methods.includes('COMPARAISON') ? (
                  <div className="sm:col-span-1">
                    <Label>Parcelle arrondie</Label>
                    <p className="mt-1 text-xs text-slate-500">
                      Fournie par la comparaison foncière.
                    </p>
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="cost-parcelle">Parcelle arrondie (facultatif)</Label>
                    <Input
                      id="cost-parcelle"
                      inputMode="decimal"
                      value={costParcelle}
                      onChange={(e) => setCostParcelle(e.target.value)}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      N&apos;affecte que la valeur vénale ; sans effet sur la valeur de mise en
                      valeur.
                    </p>
                  </div>
                )}
              </div>
            </fieldset>
          ) : null}

          {methods.includes('CAPITALISATION') ? (
            <fieldset className="space-y-2 rounded-md border border-slate-200 p-3">
              <legend className="px-1 text-xs uppercase tracking-wide text-slate-400">
                Capitalisation
              </legend>
              <div className="grid gap-2 sm:grid-cols-3">
                <div>
                  <Label htmlFor="cap-base">Base de revenu</Label>
                  <Input
                    id="cap-base"
                    inputMode="decimal"
                    value={capBase}
                    onChange={(e) => setCapBase(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="cap-mult">Multiplicateur</Label>
                  <Input
                    id="cap-mult"
                    inputMode="decimal"
                    value={capMultiplier}
                    onChange={(e) => setCapMultiplier(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="cap-set">Jeu de coefficients (facultatif)</Label>
                  <Input id="cap-set" value={capSet} onChange={(e) => setCapSet(e.target.value)} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Facteurs de revenu</Label>
                {capFactors.map((factor, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      inputMode="decimal"
                      aria-label={`Facteur de revenu ${i + 1}`}
                      value={factor}
                      onChange={(e) =>
                        setCapFactors((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={`Retirer le facteur ${i + 1}`}
                      disabled={capFactors.length === 1}
                      onClick={() => setCapFactors((prev) => prev.filter((_, j) => j !== i))}
                    >
                      Retirer
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCapFactors((prev) => [...prev, ''])}
                >
                  Ajouter un facteur
                </Button>
              </div>
            </fieldset>
          ) : null}

          <Button type="button" onClick={runPrepare} disabled={!canPrepare}>
            {prepare.isPending ? 'Préparation…' : 'Préparer'}
          </Button>

          {prepare.isError ? (
            <Alert variant="error" details={toDisplayError(prepare.error).details}>
              {toDisplayError(prepare.error).message}
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {run ? (
        <>
          {/* B. WARNINGS + BLOCKING ERRORS ----------------------------------- */}
          {run.errors.length > 0 || run.warnings.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Contrôles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {run.errors.length > 0 ? (
                  <Alert variant="error" title="Erreurs bloquantes — le calcul est bloqué">
                    <ul className="list-disc pl-5">
                      {run.errors.map((e, i) => (
                        <li key={i}>
                          <span className="font-mono text-xs">{e.code}</span> — {e.message}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}
                {run.warnings.length > 0 ? (
                  <Alert variant="warning" title="Avertissements">
                    <ul className="list-disc pl-5">
                      {run.warnings.map((w, i) => (
                        <li key={i}>
                          <span className="font-mono text-xs">{w.code}</span> — {w.message}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {/* C. EXÉCUTION ---------------------------------------------------- */}
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle>Exécution</CardTitle>
                <Badge tone={statusTone(run.status)}>{CALC_STATUS_LABELS[run.status]}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                type="button"
                onClick={runExecute}
                disabled={run.status !== 'PREPARE' || execute.isPending}
              >
                {execute.isPending ? 'Exécution…' : 'Exécuter'}
              </Button>
              {run.status === 'BLOQUE' ? (
                <p className="text-sm text-rose-600">
                  Ce calcul est bloqué par des erreurs : corrigez la saisie puis préparez à nouveau.
                </p>
              ) : null}
              {execute.isError ? (
                <Alert variant="error" details={toDisplayError(execute.error).details}>
                  {toDisplayError(execute.error).message}
                </Alert>
              ) : null}

              {run.methods.length > 0 ? (
                <MethodTable methods={run.methods} />
              ) : (
                <p className="text-sm text-slate-500">
                  Aucun résultat pour le moment — exécutez le calcul.
                </p>
              )}
            </CardContent>
          </Card>

          {/* D. SYNTHÈSE ----------------------------------------------------- */}
          {run.methods.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Synthèse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-slate-600">Valeur retenue (publiée) :</span>
                  <span className="text-lg font-semibold">{money(run.finalValue)}</span>
                  {run.calculationHash ? (
                    <span className="ml-auto font-mono text-xs text-slate-400">
                      #{run.calculationHash.slice(0, 10)}…
                    </span>
                  ) : null}
                </div>

                {run.adjustments.length > 0 ? (
                  <div className="space-y-2 rounded-md border border-slate-200 p-3">
                    <p className="text-xs uppercase tracking-wide text-slate-400">
                      Ajustements de la valeur publiée
                    </p>
                    {run.adjustments.map((a, i) => (
                      <p key={i} className="text-sm text-slate-700">
                        {money(a.automaticValue)} → <strong>{money(a.publishedValue)}</strong> —{' '}
                        {a.justification}
                      </p>
                    ))}
                  </div>
                ) : null}

                {/* Published-value adjustment form (only CALCULE / EN_REVUE). */}
                {status === 'CALCULE' || status === 'EN_REVUE' ? (
                  <fieldset className="space-y-2 border-t border-slate-100 pt-3">
                    <legend className="text-xs uppercase tracking-wide text-slate-400">
                      Ajuster la valeur publiée
                    </legend>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="adj-value">Valeur publiée</Label>
                        <Input
                          id="adj-value"
                          inputMode="decimal"
                          value={publishedValue}
                          onChange={(e) => setPublishedValue(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="adj-just">Justification (obligatoire)</Label>
                        <Input
                          id="adj-just"
                          value={adjustJustification}
                          onChange={(e) => setAdjustJustification(e.target.value)}
                        />
                      </div>
                    </div>
                    {publishedValue.trim() !== '' && adjustJustification.trim() === '' ? (
                      <p className="text-xs text-rose-600">
                        Une justification est obligatoire pour ajuster la valeur publiée.
                      </p>
                    ) : null}
                    <Button type="button" onClick={runAdjust} disabled={!canAdjust}>
                      {adjust.isPending ? 'Enregistrement…' : 'Ajuster'}
                    </Button>
                    {adjust.isError ? (
                      <Alert variant="error" details={toDisplayError(adjust.error).details}>
                        {toDisplayError(adjust.error).message}
                      </Alert>
                    ) : null}
                  </fieldset>
                ) : null}

                {/* Workflow: review / validate / finalize (gated by status). */}
                <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={status !== 'CALCULE' || review.isPending}
                    onClick={() => review.mutate(run.id, { onSuccess: (data) => setRun(data) })}
                  >
                    Passer en revue
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={status !== 'EN_REVUE' || validate.isPending}
                    onClick={() => validate.mutate(run.id, { onSuccess: (data) => setRun(data) })}
                  >
                    Valider
                  </Button>
                  <Button
                    type="button"
                    disabled={status !== 'VALIDE' || !run.finalizable || finalize.isPending}
                    onClick={() => finalize.mutate(run.id, { onSuccess: (data) => setRun(data) })}
                  >
                    Finaliser
                  </Button>
                </div>
                {review.isError ? (
                  <Alert variant="error">{toDisplayError(review.error).message}</Alert>
                ) : null}
                {validate.isError ? (
                  <Alert variant="error">{toDisplayError(validate.error).message}</Alert>
                ) : null}
                {finalize.isError ? (
                  <Alert variant="error">{toDisplayError(finalize.error).message}</Alert>
                ) : null}
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}

      {/* E. HISTORIQUE ------------------------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des calculs</CardTitle>
        </CardHeader>
        <CardContent>
          {history.isLoading ? (
            <p className="text-sm text-slate-500">Chargement…</p>
          ) : history.isError ? (
            <Alert variant="error">{toDisplayError(history.error).message}</Alert>
          ) : history.data && history.data.data.length > 0 ? (
            <ul className="space-y-2">
              {history.data.data.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 p-3 text-sm"
                >
                  <Badge tone={statusTone(r.status)}>{CALC_STATUS_LABELS[r.status]}</Badge>
                  {r.supersedesCalcRunId ? <Badge tone="warning">Remplace</Badge> : null}
                  <span className="font-semibold">{money(r.finalValue)}</span>
                  {r.calculationHash ? (
                    <span className="font-mono text-xs text-slate-400">
                      #{r.calculationHash.slice(0, 10)}…
                    </span>
                  ) : null}
                  <span className="ml-auto text-xs text-slate-500">
                    {r.createdById} — {new Date(r.createdAt).toLocaleString('fr-FR')}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Aucun calcul pour ce dossier.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Per-method raw / rounded / published values — server-computed, display only. */
function MethodTable({ methods }: { methods: MethodRunDto[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-3">Méthode</th>
            <th className="py-2 pr-3">Valeur brute</th>
            <th className="py-2 pr-3">Valeur arrondie</th>
            <th className="py-2 pr-3">Valeur publiée</th>
          </tr>
        </thead>
        <tbody>
          {methods.map((m) => (
            <tr key={m.id} className="border-b border-slate-100">
              <td className="py-2 pr-3 font-medium text-slate-800">{m.methodCode}</td>
              <td className="py-2 pr-3">{money(m.rawValue)}</td>
              <td className="py-2 pr-3">{money(m.roundedValue)}</td>
              <td className="py-2 pr-3 font-semibold">{money(m.publishedValue)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
